import React, { useCallback } from 'react';
import { Asset as ContentfulAsset } from 'contentful';
import { Asset } from '../AtomicComponents/Asset';
import { ScrollReveal } from '../ScrollReveal';
import {
  trackMediaDownload,
  getAssetUrl
} from '../../utils/analytics/trackMediaDownload';
import { IDownloadableMediaFields } from '@/types/contentful';

interface DownloadableMediaProps
  extends Omit<IDownloadableMediaFields, 'contentfulLabel'> {
  componentLocation?: string;
}

function getMediaTitle(asset: ContentfulAsset): string {
  return asset.fields?.title ?? asset.fields?.description ?? 'Untitled media';
}

export function DownloadableMedia({
  media,
  displayTitle,
  thumbnail,
  componentLocation
}: Readonly<DownloadableMediaProps>) {
  const contentType = (media?.fields?.file?.contentType as string) ?? '';
  const rawUrl = (media?.fields?.file?.url as string) ?? '';
  const assetUrl = getAssetUrl(rawUrl);
  const title = displayTitle || (media ? getMediaTitle(media) : '');
  const hasThumbnail = !!thumbnail;
  const isImage = contentType.startsWith('image/') || hasThumbnail;
  const isVideo = !hasThumbnail && contentType.startsWith('video/');
  const isPdf = !hasThumbnail && contentType === 'application/pdf';
  const fileSize = media?.fields?.file?.details?.size as number | undefined;

  const handleDownloadClick = useCallback(() => {
    trackMediaDownload({
      assetUrl: rawUrl,
      assetTitle: title,
      contentType,
      fileSizeBytes: fileSize,
      componentLocation: componentLocation ?? 'page_block'
    });

    globalThis.window.open(assetUrl, '_blank', 'noopener,noreferrer');
  }, [rawUrl, title, contentType, fileSize, componentLocation, assetUrl]);

  if (!media) return null;

  return (
    <ScrollReveal>
      <div data-testid="downloadable-media" className="group relative">
        <button
          type="button"
          onClick={handleDownloadClick}
          className="bg-transparent relative block w-full cursor-pointer border-0 p-0 text-left"
          aria-label={`Download ${title}`}
        >
          {(isImage || isVideo) && (
            <div className="relative overflow-hidden rounded-[3.5rem] tablet:rounded-[4rem]">
              <Asset asset={thumbnail ?? media} className="max-w-full" />
              <DownloadOverlay title={title} />
            </div>
          )}
          {isPdf && (
            <div className="border-gray-200 bg-gray-50 group-hover:border-green-300 group-hover:bg-green-50 flex items-center gap-3 rounded-2xl border p-4 transition-colors">
              <PdfIcon />
              <span className="text-gray-700 group-hover:text-green-700 text-sm font-medium">
                {title}
              </span>
              <DownloadArrowIcon className="ml-auto" />
            </div>
          )}
          {!isImage && !isVideo && !isPdf && (
            <div className="border-gray-200 bg-gray-50 group-hover:border-green-300 group-hover:bg-green-50 flex items-center gap-3 rounded-2xl border p-4 transition-colors">
              <FileIcon />
              <span className="text-gray-700 group-hover:text-green-700 text-sm font-medium">
                {title}
              </span>
              <DownloadArrowIcon className="ml-auto" />
            </div>
          )}
        </button>
      </div>
    </ScrollReveal>
  );
}

function DownloadOverlay({ title }: Readonly<{ title: string }>) {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-end justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
      <div className="mb-6 flex items-center gap-2 rounded-full bg-black/70 px-4 py-2 backdrop-blur-sm">
        <DownloadArrowIcon className="text-white" />
        <span className="max-w-[200px] truncate text-sm font-medium text-white">
          {title}
        </span>
      </div>
    </div>
  );
}

function DownloadArrowIcon({
  className = ''
}: Readonly<{ className?: string }>) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`shrink-0 ${className}`}
      aria-hidden="true"
    >
      <path
        d="M21 14C20.7348 14 20.4804 14.1054 20.2929 14.2929C20.1054 14.4804 20 14.7348 20 15V19C20 19.2652 19.8946 19.5196 19.7071 19.7071C19.5196 19.8946 19.2652 20 19 20H3C2.73478 20 2.48043 19.8946 2.29289 19.7071C2.10536 19.5196 2 19.2652 2 19V15C2 14.7348 1.89464 14.4804 1.70711 14.2929C1.51957 14.1054 1.26522 14 1 14C0.734784 14 0.48043 14.1054 0.292893 14.2929C0.105357 14.4804 0 14.7348 0 15V19C0 19.7956 0.31607 20.5587 0.87868 21.1213C1.44129 21.6839 2.20435 22 3 22H19C19.7956 22 20.5587 21.6839 21.1213 21.1213C21.6839 20.5587 22 19.7956 22 19V15C22 14.7348 21.8946 14.4804 21.7071 14.2929C21.5196 14.1054 21.2652 14 21 14Z"
        fill="currentColor"
      />
      <path
        d="M10.232 16.64C10.3258 16.7525 10.4433 16.8431 10.576 16.9052C10.7087 16.9673 10.8535 16.9995 11 16.9995C11.1465 16.9995 11.2913 16.9673 11.424 16.9052C11.5567 16.8431 11.6741 16.7525 11.768 16.64L16.768 10.64C16.8896 10.494 16.9671 10.3164 16.9914 10.1279C17.0157 9.93946 16.9858 9.74799 16.9052 9.57591C16.8247 9.40383 16.6967 9.25827 16.5364 9.15627C16.3761 9.05428 16.19 9.00007 16 9H12V1C12 0.734784 11.8946 0.48043 11.7071 0.292893C11.5196 0.105357 11.2652 0 11 0C10.7348 0 10.4804 0.105357 10.2929 0.292893C10.1053 0.48043 9.99999 0.734784 9.99999 1V9H5.99999C5.80998 9.00007 5.62392 9.05428 5.4636 9.15627C5.30328 9.25827 5.17533 9.40383 5.09475 9.57591C5.01416 9.74799 4.98428 9.93946 5.00858 10.1279C5.03289 10.3164 5.11038 10.494 5.23199 10.64L10.232 16.64Z"
        fill="currentColor"
      />
    </svg>
  );
}

function PdfIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-red-500 shrink-0"
      aria-hidden="true"
    >
      <path
        d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 2V8H20"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text
        x="12"
        y="17"
        textAnchor="middle"
        fontSize="6"
        fontWeight="bold"
        fill="currentColor"
      >
        PDF
      </text>
    </svg>
  );
}

function FileIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-gray-500 shrink-0"
      aria-hidden="true"
    >
      <path
        d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 2V8H20"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
